import type { ItemId } from '@/data/items';
import type { NpcId } from '@/data/npcs';

/**
 * Quests.
 *
 * A quest is a list of stages, each of which asks for something you can be
 * carrying. That is a deliberate limit: "bring me four of these" needs no
 * counter anywhere in the world, so a stage can be checked by looking in the
 * backpack and nothing has to be tracked while you play. What makes a chain
 * feel like a journey is not the verb, it is where the materials come from —
 * these three walk from the grass outside the door down to the deep floors.
 *
 * Offered on friendship rather than on a flag, so the first quest in the game
 * is something you earn by turning up rather than something you trip over.
 */

export type QuestId = 'mira-blade';

export interface QuestStage {
  /** What she asks for, and how much of it. */
  readonly item: ItemId;
  readonly count: number;
  /** What she says while this stage is the current one. */
  readonly ask: string;
  /** And when you hand it over. */
  readonly done: string;
}

export interface QuestDefinition {
  readonly id: QuestId;
  readonly giver: NpcId;
  readonly name: string;
  /** Friendship points before it is offered at all. */
  readonly requiresPoints: number;
  /** What she says when it is done and there is nothing left to bring. */
  readonly afterword: string;
  readonly stages: readonly QuestStage[];
  readonly reward: {
    readonly coins: number;
    readonly friendship: number;
    /** A recipe id that this quest, and only this quest, unlocks. */
    readonly recipe: string;
  };
}

export const QUESTS: Readonly<Record<QuestId, QuestDefinition>> = {
  'mira-blade': {
    id: 'mira-blade',
    giver: 'mira',
    name: 'What the Tide Owes',
    // Roughly a week of saying hello, or two or three good gifts. Enough that
    // she is not handing her father's work to a stranger.
    requiresPoints: 80,
    afterword: 'Go on then. Put it to work and tell me what it wants next.',
    stages: [
      {
        item: 'mat:fiber',
        count: 10,
        ask: 'Cord first. Ten lengths of fiber, and I will show you what I am at.',
        done: 'Good cord. That is the handle sorted, near enough.',
      },
      {
        item: 'mat:blightSap',
        count: 4,
        ask: 'Now the strange part. Four of that sap, from whatever is under the ground.',
        done: 'It went in hissing and came out *singing*. I have never had that.',
      },
      {
        item: 'mat:mothDust',
        count: 6,
        ask: 'Six of the glowing dust. Deeper down, and mind yourself getting it.',
        done: 'There. Not my father’s pattern — mine. And now yours, if you want it.',
      },
    ],
    reward: {
      coins: 250,
      friendship: 60,
      recipe: 'emberbrand',
    },
  },
};

export const QUEST_IDS = Object.keys(QUESTS) as QuestId[];

/** Every quest a person gives, in the order they were written. */
export function questsFrom(giver: NpcId): QuestDefinition[] {
  return QUEST_IDS.map((id) => QUESTS[id]).filter((quest) => quest.giver === giver);
}
